import React, { Component } from 'react'
import { connect } from "react-redux"

import trans from "trans"
import dayjs, { Dayjs } from "dayjs"
import { enGB, cs, sk, pl } from "date-fns/locale"
import _debounce from "lodash/debounce";

import {
    css,
    TextInput,
    Grid,
    GridCell,
    LittleStatus,
    InputGroup,
    FieldLabel,
} from "@bdfx/mergado-ui-kit"

import DayPicker, { DateUtils } from 'react-day-picker';
import { style as pickerStyle } from "./datepicker-style"

import { updateInputValue as change } from "app/reducers/form"
import { Template } from "app/types/model-types"
import { getDateToday } from "app/helpers/other";
import { createToast } from "actions";
import { setActiveGroupId, setActiveShapeId } from "shape-edit/reducers";
import { selectFormValues } from 'app/reducers/form'
import { DATABASE_DATE_FORMAT, dateToDBFormat, getDatePattern, parseDate } from 'shape-edit/helpers/locale'
import { getLocale } from 'app/helpers/session'

interface Props {
    templateForm: Template
    change: typeof change
    setActiveShapeId: typeof setActiveShapeId
    setActiveGroupId: typeof setActiveGroupId
}

interface State {
    from: any
    to: any
    isLoaded: boolean
    showDateLimit: boolean
    locale: any
    datePattern: string
}

class FormatsForm extends Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            from: undefined,
            to: undefined,
            isLoaded: false,
            showDateLimit: !!(props.templateForm.active_from || props.templateForm.active_to),
            locale: null,
            datePattern: DATABASE_DATE_FORMAT,
        }
    }

    componentDidMount(): void {
        this.setLocale()
    }

    setLocale = () => {
        this.setState({ datePattern: getDatePattern(getLocale()) })

        const locale = dayjs().locale()
        if (locale === "cs") {
            import('dayjs/locale/cs').then(e => {
                this.setState({ locale: e.default })
            })
        } else if (locale === "sk") {
            import('dayjs/locale/sk').then(e => {
                this.setState({ locale: e.default })
            })
        } else if (locale === "pl") {
            import('dayjs/locale/pl').then(e => {
                this.setState({ locale: e.default })
            })
        } else {
            import('dayjs/locale/en').then(e => {
                this.setState({ locale: e.default })
            })
        }
    }

    handleDayClick = (day) => {
        const range = DateUtils.addDayToRange(day, { from: this.state.from, to: this.state.to });
        this.setState({
            from: range.from,
            to: range.to,
        });

        this.props.change('templateForm', 'active_from', range.from ? dayjs(range.from).format("YYYY-MM-DD") : null)
        this.props.change('templateForm', 'active_to', range.to ? dayjs(range.to).format("YYYY-MM-DD") : null)


    }

    setFrom = _debounce((value) => {
        const { datePattern } = this.state

        if (value === '') {
            this.setState({ from: null })
            this.props.change('templateForm', 'active_from', null)
        } else {
            const date = parseDate(value, datePattern)
            if (date) {
                this.setState({ from: date })
                const formatted = dateToDBFormat(date)
                this.props.change('templateForm', 'active_from', formatted)
            } else {
                this.setState({ from: null })
                this.props.change('templateForm', 'active_from', null)
            }
        }
    }, 1000)

    setTo = _debounce((value) => {
        const { datePattern } = this.state

        if (value === '') {
            this.setState({ to: null })
            this.props.change('templateForm', 'active_to', null)
        } else {
            const date = parseDate(value, datePattern)
            if (date) {
                this.setState({ to: date })
                const formatted = dateToDBFormat(date)
                this.props.change('templateForm', 'active_to', formatted)
            } else {
                this.setState({ to: null })
                this.props.change('templateForm', 'active_to', null)
            }
        }
    }, 1000)

    render() {
        const { templateForm } = this.props
        const { showDateLimit, from, to, locale, datePattern } = this.state

        const dateNow = getDateToday()
        const activeFrom = templateForm.active_from ? new Date(templateForm.active_from) : null
        const activeTo = templateForm.active_to ? new Date(templateForm.active_to) : null
        const isActiveNow = ((!activeFrom || (activeFrom <= dateNow)) && (!activeTo || (activeTo >= dateNow)))
        const modifiers = { start: from, end: to };

        const isValidFrom = dayjs(this.state.from).isValid()
        const isValidTo = dayjs(this.state.to).isValid()

        const textFrom = templateForm.active_from ? (isValidFrom ? dayjs(templateForm.active_from).format(datePattern) : from) : null
        const textTo = templateForm.active_to ? (isValidTo ? dayjs(templateForm.active_to).format(datePattern) : to) : null

        return (
            <StyledDiv className={'hide-buttons horizontal-fixed-size'}>
                <InputGroup
                    orientation="horizontal"
                    singleChoice={true}
                    value={showDateLimit}
                    onChange={(e) => {
                        if (!e) {
                            this.props.change('templateForm', 'active_from', null)
                            this.props.change('templateForm', 'active_to', null)
                            this.setState({
                                from: null,
                                to: null,
                            });
                        }
                        this.setState({ showDateLimit: e ? true : false })
                    }}
                    items={[
                        { value: false, label: trans('active_now') },
                        { value: true, label: trans('set_timer') },
                    ]} />
                <br />
                <FieldLabel>{trans('set_active_date')}

                    {(templateForm.active_from || templateForm.active_to) &&
                        <LittleStatus type={isActiveNow ? 'success' : 'warning'}
                            title={isActiveNow ? trans('is_active') : trans('not_active')}
                            style={{ float: 'right', }}
                        />
                    }
                </FieldLabel>

                <GridCell col={'span 2'} className={'date-range-form'}>
                    <Grid gap="20px" cols="1fr 1fr">
                        <TextInput
                            key={showDateLimit + "from"}
                            placeholder={datePattern}
                            disabled={!showDateLimit}
                            value={textFrom}
                            onChange={this.setFrom}
                            invalid={textFrom && !isValidFrom}
                        />
                        <TextInput
                            key={showDateLimit + "to"}
                            placeholder={datePattern}
                            disabled={!showDateLimit}
                            value={textTo}
                            onChange={this.setTo}
                            invalid={textTo && !isValidTo}
                        />
                    </Grid>
                </GridCell>

                <GridCell col={'1/-1'} style={{ paddingBottom: '15px' }} />

                <DatePickerStyle style={pickerStyle}>
                    <DayPicker
                        onDayClick={this.handleDayClick}
                        selectedDays={[from, { from, to }]}
                        className={`muk-datepicker-reactdatepicker ${!showDateLimit ? "disabled" : ""}`}
                        modifiers={modifiers}
                        todayButton={trans("today")}
                        {...{
                            firstDayOfWeek: 1,
                            numberOfMonths: 2,
                            months: locale?.months,
                            weekdaysLong: locale?.weekdays,
                            weekdaysShort: locale?.weekdaysShort,
                        }}
                        locale={locale}
                    />
                </DatePickerStyle>
            </StyledDiv>
        )
    }
}

const DatePickerStyle = css("div")({

    "div.DayPicker-Month": {
        '@media screen and (max-width: 1800px)': {
            width: "100%",
        },
        width: "calc(50% - 2rem)",
        marginBottom: "20px",
        marginTop: "20px",

    },
    ".DayPicker.muk-datepicker-reactdatepicker": {
        display: "block",
    },
    ".disabled": {
        opacity: 0.3,
        pointerEvents: "none",
    }
})

const StyledDiv = css("div")({
    fontSize: '14px',
    ".DayPicker-TodayButton": {
        border: "1px solid #4a90e2 !important",
        padding: "8px 40px",
        borderRadius: "5px",
        textAlign: "center",
        display: "block",
        margin: "auto",
        fontSize: "14px !important",
    },
    ' .muk-datepicker': {
        position: 'unset',
    },
    ' .muk-datepicker-popover': {
        maxWidth: '100%',
        fontSize: '9px',
        overflowX: 'auto',
    },
    ' .muk-datepicker-picker': {
        border: '1px solid #ccc',
    },
})

function mapStateToProps(state) {
    return {
        templateForm: selectFormValues(state, 'templateForm'),
    }
}

export default connect(mapStateToProps, {
    change,
    createToast,
    setActiveShapeId,
    setActiveGroupId,
})(FormatsForm)
